import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { Button, Dropdown, Modal, notification, Typography } from "antd";
import {
  AMENDMENT_LIEN_COST_DELETE_ENDPOINT,
  AMENDMENT_LIEN_COST_LIST_ENDPOINT,
} from "apiServices/API_ENDPOINTS";
import { deleteData, getData } from "apiServices/common";
import AppContent from "common/AppContent";
import ListTable from "common/ListTable";
import AppPageHeader from "common/PageHeader";
import ExtraContent from "components/Configuration/CommonComponents/ExtraContent";
import FilterComponent from "components/Configuration/CommonComponents/FilterComponent";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  SALES_CONTRACT_AMENDMENT_LIEN_COST_CREATE_PERMISSION,
  SALES_CONTRACT_AMENDMENT_LIEN_COST_DELETE_PERMISSION,
  SALES_CONTRACT_AMENDMENT_LIEN_COST_EDIT_PERMISSION,
  SALES_CONTRACT_AMENDMENT_LIEN_COST_VIEW_PERMISSION,
} from "routes/permissions";
import { hasPermission } from "utils/functions";

export default function AmendmentLienCost() {
  // Router
  const location = useLocation();
  const navigate = useNavigate();
  const routerPath = location ? location.pathname : null;

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Antd
  const { Title } = Typography;

  // Redux
  const filterData = useSelector(({ filter }) => filter.filterData);

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      localStorage.setItem(
        "filter_status",
        JSON.stringify({
          ...parsedFilterStatus,
          [routerPath]: true,
        }),
      );
    } else {
      localStorage.setItem(
        "filter_status",
        JSON.stringify({
          ...parsedFilterStatus,
          [routerPath]: false,
        }),
      );
    }
  }, [showFilter, routerPath]);

  const getAllAmendmentLienCosts = useCallback(
    async (filterValues) => {
      const { sales_contract_id } = filterValues || {};

      setLoading(true);
      setFetchError(null);

      const query = `${AMENDMENT_LIEN_COST_LIST_ENDPOINT}?page=${currentPage}`;
      const bodyData = {
        ...(sales_contract_id && { sales_contract_id }),
      };

      const response = await getData(query, false, bodyData);
      if (response?.status === 200) {
        const data = response.data?.data?.data || [];
        // Add unique key if id is missing
        const dataWithKeys = data.map((item, index) => ({
          ...item,
          key: item.id || `amendment-lien-cost-${index}`,
        }));

        setTableData(dataWithKeys);
        setMetaData({
          current: response.data?.data?.current_page || 1,
          defaultCurrent: response?.data?.data?.from || 1,
          defaultPageSize: response?.data?.data?.per_page || 10,
          total: response.data?.data?.total || 0,
        });
        setLoading(false);
      } else {
        setFetchError("Could not load this list.");
        setLoading(false);
      }
    },
    [currentPage],
  );

  // Effects
  useEffect(() => {
    getAllAmendmentLienCosts(filterData);
  }, [refresh, filterData, getAllAmendmentLienCosts]);

  const refetch = useCallback(() => {
    setRefresh((prev) => prev + 1);
  }, []);

  const deleteSelectedAmendmentLienCost = useCallback(
    async (id) => {
      Modal.confirm({
        title: "Confirm Deletion",
        content: "Are you sure you want to delete this Amendment Lien Cost?",
        okText: "Yes, Delete",
        okType: "danger",
        cancelText: "Cancel",
        onOk: async () => {
          const response = await deleteData(
            `${AMENDMENT_LIEN_COST_DELETE_ENDPOINT}/${id}`,
          );

          if (
            response &&
            (response?.code === 200 || response?.status === 200)
          ) {
            setLoading(false);
            notification.success({
              message: "Success",
              description: "Amendment Lien Cost deleted successfully",
              placement: "topRight",
            });
            refetch();
          }
        },
        onCancel: () => {
          setLoading(false);
        },
      });
    },
    [refetch],
  );

  const navigateToAdd = useCallback(() => {
    navigate("/import-export-expense-tracking/amendment-lien-cost/add");
  }, [navigate]);

  const navigateToEdit = useCallback(
    (record) => {
      navigate(
        `/import-export-expense-tracking/amendment-lien-cost/edit/${record.id}`,
      );
    },
    [navigate],
  );

  const navigateToView = useCallback(
    (record) => {
      navigate(
        `/import-export-expense-tracking/amendment-lien-cost/view/${record.id}`,
      );
    },
    [navigate],
  );

  const changePage = (newPage) => {
    setCurrentPage(newPage);
  };

  const tableColumns = useMemo(
    () => [
      {
        title: "Sales Contract No",
        dataIndex: ["sales_contract", "sale_contract_info", "reference_no"],
        width: "15%",
        key: "salesContractNumber",
        ellipsis: true,
        render: (tagNumber) => tagNumber || "N/A",
      },
      {
        title: "Contract Value",
        dataIndex: "sales_contract_value",
        width: "15%",
        key: "salesContractValue",
        ellipsis: true,
        render: (value) => (value ? `${value} $` : "N/A"),
      },
      {
        title: "Tag Number",
        dataIndex: ["sales_contract", "tag_number"],
        width: "15%",
        key: "tag_number",
      },
      {
        title: "TRN Date",
        dataIndex: "trn_date",
        width: "12%",
        key: "trnDate",
        ellipsis: true,
        render: (date) => date || "N/A",
      },
      {
        title: "Total Charge",
        dataIndex: "lien_charges",
        width: "15%",
        key: "totalAmount",
        ellipsis: true,
        render: (lienCharges) => {
          if (!lienCharges || !Array.isArray(lienCharges)) return "N/A";
          const total = lienCharges.reduce((sum, charge) => {
            return sum + (charge.exim_charge_amount || 0);
          }, 0);
          return total > 0 ? `${total} BDT` : "N/A";
        },
      },
      {
        title: "Action",
        key: "action",
        width: "10%",
        render: (_, record) => {
          const menuItems = [
            {
              key: "view",
              label: "View",
              icon: <EyeOutlined />,
              onClick: () => navigateToView(record),
              disabled: !hasPermission([
                SALES_CONTRACT_AMENDMENT_LIEN_COST_VIEW_PERMISSION,
              ]),
            },
            {
              key: "edit",
              label: "Edit",
              icon: <EditOutlined />,
              onClick: () => navigateToEdit(record),
              disabled: !hasPermission([
                SALES_CONTRACT_AMENDMENT_LIEN_COST_EDIT_PERMISSION,
              ]),
            },
            {
              type: "divider",
            },
            {
              key: "delete",
              label: "Delete",
              icon: <DeleteOutlined />,
              onClick: () => deleteSelectedAmendmentLienCost(record.id),
              disabled: !hasPermission([
                SALES_CONTRACT_AMENDMENT_LIEN_COST_DELETE_PERMISSION,
              ]),
              danger: true,
            },
          ];

          return (
            <Dropdown
              menu={{ items: menuItems }}
              placement="bottomRight"
              trigger={["hover"]}
            >
              <Button type="default" icon={<MoreOutlined />} />
            </Dropdown>
          );
        },
      },
    ],
    [deleteSelectedAmendmentLienCost, navigateToEdit, navigateToView],
  );

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={getAllAmendmentLienCosts}
        filterName="amendment_lien_cost"
        showFilter={showFilter}
        setShowFilter={setShowFilter}
      />
    ),
    [showFilter, tableData, getAllAmendmentLienCosts],
  );

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={navigateToAdd}
                disabled={
                  !hasPermission([
                    SALES_CONTRACT_AMENDMENT_LIEN_COST_CREATE_PERMISSION,
                  ])
                }
                addButtonText={
                  hasPermission([
                    SALES_CONTRACT_AMENDMENT_LIEN_COST_CREATE_PERMISSION,
                  ])
                    ? "Add New"
                    : "No Permission"
                }
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
            }
            title={<Title level={5}>Amendment Lien Cost</Title>}
          />
        }
        content={
          <>
            {memoizedCustomFilter}

            <div className="table" style={{ marginTop: 10 }}>
              <ListTable
                loading={loading}
                error={fetchError}
                onRetry={() => getAllAmendmentLienCosts(filterData)}
                dataSource={tableData}
                columns={tableColumns}
                rowKey="id"
                pagination={{
                  showSizeChanger: false,
                  defaultPageSize: metaData?.defaultPageSize || 10,
                  current: metaData?.current || 1,
                  onChange: changePage,
                  defaultCurrent: metaData?.defaultCurrent || 1,
                  total: metaData?.total || 0,
                  showTotal: (total) => `Total ${total} items`,
                }}
                bordered
                size="middle"
                scroll={{
                  x: "max-content",
                  y: tableData.length > 10 ? 500 : undefined,
                }}
                style={{ borderRadius: "8px", overflow: "hidden" }}
              />
            </div>
          </>
        }
      />
    </>
  );
}
